// C4, C5, I2 (docs/PAID_CONTENT.md): the packs this family owns, as the phone knows them. When they
// change (bought on this phone or another, restored on a new one, refunded), the trip and kit JSON
// cached for the old answer are dropped and the pack loads again. A pack the family owns is then
// downloaded once in the background, so the whole trip works offline (its file list:
// /packs/files/<pack>.json). Skipped when the phone asks to save data.
import { api } from './api'
import { kvGet, kvSet } from './db'
import type { CatalogueEntry } from '../domain/packs'
import { confirmCheckoutReturn } from './billing'
import { usePack } from './pack'
import { usePacks, type EntitlementsResponse } from './packs'

const OWNED_KEY = 'ownedPacks'
const CATALOGUE_KEY = 'packCatalogue'
const doneKey = (pack: string) => `packFiles:${pack}`
const AT_ONCE = 4
/** The download waits for the app to settle after start; the e2e build never downloads (its runs are short). */
const DOWNLOAD_AFTER_MS = 30_000
const DOWNLOADS = import.meta.env.MODE !== 'e2e'

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i])

/** The service worker's runtime cache for /packs (src/sw.ts): its trip and kit JSON, forgotten. */
async function dropCachedPackJson(): Promise<void> {
  if (typeof caches === 'undefined' || !(await caches.has('packs'))) return
  const cache = await caches.open('packs')
  for (const req of await cache.keys()) {
    const path = new URL(req.url).pathname
    if (path.endsWith('/pack.json') || path.startsWith('/packs/kits/')) await cache.delete(req)
  }
}

async function download(pack: string): Promise<void> {
  if ((await kvGet<boolean>(doneKey(pack))) || (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData) return
  const list = await api<{ files: string[] }>(`/packs/files/${pack}.json`).catch(() => null)
  if (!list) return
  const queue = [...list.files]
  let failed = 0
  // Each fetch goes through the service worker, which keeps it in the cache for its folder.
  await Promise.all(
    Array.from({ length: AT_ONCE }, async () => {
      for (let f = queue.shift(); f; f = queue.shift()) {
        const ok = await fetch(f)
          .then((r) => r.ok)
          .catch(() => false)
        if (!ok) failed++
      }
    }),
  )
  // A file that didn't come (offline half way) means trying again next start.
  if (!failed) await kvSet(doneKey(pack), true)
}

/** True when this start brought a pack (a Stripe Checkout just came back): the caller says so. */
export async function syncOwnedPacks(): Promise<boolean> {
  const before = await kvGet<string[]>(OWNED_KEY)
  // What the phone knew last time, so the Map wraps the right islands while offline.
  if (before && !usePacks.getState().owned) usePacks.getState().set({ owned: before, catalogue: (await kvGet<CatalogueEntry[]>(CATALOGUE_KEY)) ?? [] })
  const bought = await confirmCheckoutReturn()
  const res = await api<EntitlementsResponse>('/api/entitlements').catch(() => null)
  if (!res) return bought
  const owned = [...res.packs].sort()
  usePacks.getState().set({ owned, catalogue: res.catalogue, web: res.web, play: res.play, notify: res.notify ?? [] })
  await kvSet(CATALOGUE_KEY, res.catalogue)
  if (!before || !same(before, owned)) {
    await kvSet(OWNED_KEY, owned)
    for (const p of before ?? []) if (!owned.includes(p)) await kvSet(doneKey(p), false)
    await dropCachedPackJson()
    await usePack.getState().load()
  }
  if (!DOWNLOADS) return bought
  setTimeout(() => {
    void (async () => {
      for (const p of owned) await download(p)
    })()
  }, DOWNLOAD_AFTER_MS)
  return bought
}
